import { SpinnerIcon } from '@acceso/design-system';
import clsx from 'clsx';
import React, { Suspense, useEffect } from 'react';
import { Group, Panel } from 'react-resizable-panels';
import { useParams } from 'react-router-dom';

import {
  TProgramBroadcast,
  TProgramBroadcastSegmentsPatch,
} from '@/features/broadcast-schedule/types';
import { BroadcastScheduleProvider } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/context/BroadcastScheduleProvider';
import { usePatchSegment } from '@/features/segments/api/patch-segment';
import { useReport } from '@/stores/report-store';

import { useBroadcastScheduleProgram } from '../../context/BroadcastScheduleProgramContext';
import { useCutsListSource } from '../../hooks/use-cuts-list-source';
import { useNavigationList } from '../../hooks/useNavigationList';

import { BroadcastScheduleProgramBodySegmentCard } from './components/BroadcastScheduleProgramBodySegmentCard';
import { BroadcastScheduleProgramBodySegmentDetailCard } from './components/BroadcastScheduleProgramBodySegmentDetailCard/BroadcastScheduleProgramBodySegmentDetailCard';
import { BroadcastScheduleProgramBodySegmentToolkit } from './components/BroadcastScheduleProgramBodySegmentToolkit/BroadcastScheduleProgramBodySegmentToolkit';
import { useBroadcastScheduleClipsFilters } from './components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitCuts/components/store/broadcast-schedule-clips-filters';
import { ESegmentToolkit } from './components/BroadcastScheduleProgramBodySegmentToolkit/types';

type TBroadcastScheduleProgramBodyProps = {
  programBroadcast?: TProgramBroadcast;
  isLoading: boolean;
};

export function BroadcastScheduleProgramBody({
  programBroadcast,
  isLoading,
}: TBroadcastScheduleProgramBodyProps) {
  const { id = '' } = useParams();

  const {
    visibleBroadcastProgramCards,
    pinnedId,
    toolkitComponents,
    toolkitCardRegionRef,
    activeSegment,
    segmentItems,
    isHeaderVisible,
    setActiveSegment,
    setSegmentItems,
    handleTooltikClick,
    setDrawerLogo,
    setVisibleBox,
  } = useBroadcastScheduleProgram();
  const { mutateAsync: patchSegment } = usePatchSegment({ id });
  const { setReport, setReportedId, setReportType } = useReport();
  const { paginationValues, filters } = useBroadcastScheduleClipsFilters();
  const orderBy =
    paginationValues.sortBy?.id && paginationValues.sortBy?.order
      ? `${paginationValues.sortBy.id} ${paginationValues.sortBy.order}`
      : undefined;
  useCutsListSource({
    kind: 'program',
    id: programBroadcast?.id,
    filters: {
      workspaces: filters.workspaces as string[],
      search: paginationValues.query,
      order_by: orderBy,
    },
  });

  const { handleGoToNext, handleGoToPrevious, handleGoToFirst, handleGoToLast } = useNavigationList(
    {
      items: segmentItems,
      activeItem: activeSegment,
      setActiveItem: setActiveSegment,
    }
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setDrawerLogo(false);
        setVisibleBox(null);
      }
      if (event.altKey && event.key === 'ArrowDown') {
        handleGoToNext();
      }
      if (event.altKey && event.key === 'ArrowUp') {
        handleGoToPrevious();
      }
      if (event.altKey && event.key === 'Home') {
        handleGoToFirst();
      }
      if (event.altKey && event.key === 'End') {
        handleGoToLast();
      }
      if (event.ctrlKey && event.key.toLowerCase() === 'e') {
        handleTooltikClick(ESegmentToolkit.CORTES);
      }
      if (event.ctrlKey && event.key === 'F12') {
        if (!activeSegment) return;

        const body: TProgramBroadcastSegmentsPatch = {
          ...activeSegment,
          review_state: 5,
          programs: activeSegment?.programs.map(program => program.id) || [],
        };

        patchSegment(body).then(data => {
          setActiveSegment(prev => {
            if (!prev) return prev;
            return { ...prev, review_state: data.review_state };
          });
          setSegmentItems(prev => {
            if (!prev) return prev;
            return prev.map(segmentItem => {
              if (segmentItem.id === data.id) {
                return {
                  ...segmentItem,
                  review_state: data.review_state,
                };
              }
              return segmentItem;
            });
          });
        });

        setReportType('segment');
        setReport(true);
        setReportedId(activeSegment.id);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    setDrawerLogo,
    setVisibleBox,
    patchSegment,
    setActiveSegment,
    setSegmentItems,
    handleGoToNext,
    handleGoToPrevious,
    handleGoToFirst,
    handleGoToLast,
    activeSegment,
  ]);
  return (
    <BroadcastScheduleProvider>
      <Group
        className={clsx(
          'flex flex-col gap-2 lg:flex-row',
          isHeaderVisible ? 'lg:max-h-[calc(100vh-170px)]' : 'lg:max-h-[calc(100vh-120px)]'
        )}
      >
        {visibleBroadcastProgramCards?.segments && (
          <Panel minSize={300} maxSize="25%">
            <BroadcastScheduleProgramBodySegmentCard
              items={segmentItems}
              isLoading={isLoading}
              programBroadcast={programBroadcast}
            />
          </Panel>
        )}
        {visibleBroadcastProgramCards?.segmentDetail && (
          <Panel minSize="35%">
            <BroadcastScheduleProgramBodySegmentDetailCard
              items={segmentItems}
              setSegmentItems={setSegmentItems}
              programBroadcast={programBroadcast as TProgramBroadcast}
            />
          </Panel>
        )}
        {pinnedId && toolkitComponents[pinnedId] && (
          <Suspense fallback={<SpinnerIcon className="animation infinite" />}>
            <Panel minSize={300}>
              <div
                ref={toolkitCardRegionRef}
                className={clsx('z-20 order-3 w-full flex-auto overflow-auto lg:order-2')}
                id="parrilla-segments-toolkit-detail"
              >
                {React.createElement(toolkitComponents[pinnedId], { programBroadcast })}
              </div>
            </Panel>
          </Suspense>
        )}
        {visibleBroadcastProgramCards?.segmentToolkit && (
          <BroadcastScheduleProgramBodySegmentToolkit />
        )}
      </Group>
    </BroadcastScheduleProvider>
  );
}
